import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import type { DataTablePageEvent } from 'primereact/datatable';
import './NADWaiverTable.styles.scss';

interface NADWaiverTableProps {
  waivers: any;
  /** Total manufacturer groups (for server-side pagination). When set with onPageChange, enables lazy pagination. */
  totalRecords?: number;
  /** Show loading overlay in table body (e.g. when fetching another page). */
  loading?: boolean;
  /** Called when user changes page; parent should fetch new page. */
  onPageChange?: (event: DataTablePageEvent) => void;
  /** Rows per page when using server-side pagination. Default 10. */
  pageSize?: number;
  /** Current search term (for server-side search). When set with onSearchChange, shows search bar. */
  searchValue?: string;
  /** Called when user changes search; parent should refetch with page 1. */
  onSearchChange?: (value: string) => void;
}

const NADWaiverTable: React.FC<NADWaiverTableProps> = ({
  waivers,
  totalRecords,
  loading = false,
  onPageChange,
  pageSize = 10,
  searchValue = '',
  onSearchChange,
}) => {
  const [selectedManufacturer, setSelectedManufacturer] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  const isServerSidePagination =
    typeof totalRecords === 'number' && typeof onPageChange === 'function';
  const currentPage = waivers?.page ?? 1;
  const rows = pageSize;

  // Group waivers by manufacturer
  const groupedWaivers = React.useMemo(() => {
    const list = waivers?.waivers ?? [];
    const grouped = new Map();

    list.forEach((waiver: any) => {
      if (!grouped.has(waiver.manufacturer)) {
        grouped.set(waiver.manufacturer, {
          manufacturer: waiver.manufacturer,
          ndcs: [],
          issue_dates: new Set(),
          exp_dates: new Set(),
        });
      }

      const group = grouped.get(waiver.manufacturer);
      group.ndcs.push(waiver);
      group.issue_dates.add(waiver.issue_date);
      group.exp_dates.add(waiver.exp_date);
    });

    return Array.from(grouped.values()).map(group => ({
      manufacturer: group.manufacturer,
      ndc_count: group.ndcs.length,
      issue_dates: Array.from(group.issue_dates),
      exp_dates: Array.from(group.exp_dates),
      ndcs: group.ndcs,
      earliest_expiry: group.ndcs.reduce((earliest: any, w: any) =>
        new Date(w.exp_date) < new Date(earliest.exp_date) ? w : earliest
      ),
    }));
  }, [waivers]);

  const formatDates = (dates: string[]) => {
    if (dates.length === 1) {
      return new Date(dates[0]).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return (
      <div>
        {dates.map((d, i) => (
          <div key={i}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
        ))}
      </div>
    );
  };

  const issueDateTemplate = (rowData: any) => {
    return (
      <div className="date-cell">
        {formatDates(rowData.issue_dates)}
      </div>
    );
  };

  const expiryDateTemplate = (rowData: any) => {
    return (
      <div className="date-cell">
        {formatDates(rowData.exp_dates)}
      </div>
    );
  };

  const actionTemplate = (rowData: any) => {
    return (
      <Button
        label="View NDCs"
        icon="pi pi-eye"
        size="small"
        outlined
        onClick={() => {
          setSelectedManufacturer(rowData);
          setShowModal(true);
        }}
      />
    );
  };

  return (
    <>
      <section className="narrative-box nad-waiver-box">
        <div className="nad-waiver-header-row">
          <h4 className="narrative-box-text-title">
            <i className="pi pi-file-edit" style={{ marginRight: '0.5rem' }} />
            NAD Waiver
          </h4>
          {typeof onSearchChange === 'function' && (
            <div className="nad-waiver-search-row">
              <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search manufacturers, NDC, dates…"
                  className="nad-waiver-search-input"
                />
              </IconField>
            </div>
          )}
        </div>

        <div className="waiver-table-container">
          <DataTable
            value={groupedWaivers}
            className="nad-waiver-table"
            emptyMessage="No active NAD waivers"
            stripedRows
            lazy={isServerSidePagination}
            paginator={isServerSidePagination}
            first={isServerSidePagination ? (currentPage - 1) * rows : 0}
            rows={isServerSidePagination ? rows : undefined}
            totalRecords={isServerSidePagination ? totalRecords : undefined}
            onPage={isServerSidePagination ? onPageChange : undefined}
            loading={loading}
          >
            <Column
              field="manufacturer"
              header="Manufacturers"
              style={{ fontWeight: 600, minWidth: '250px' }}
            />
            <Column
              field="ndc_count"
              header="NDC Count"
              body={(rowData) => (
                <Tag value={`${rowData.ndc_count}`} />
              )}
              style={{ width: '120px' }}
            />
            <Column
              header="Issue Date"
              body={issueDateTemplate}
              style={{ width: '180px' }}
            />
            <Column
              header="Expiry Date"
              body={expiryDateTemplate}
              style={{ width: '200px' }}
            />
            <Column
              header="Actions"
              body={actionTemplate}
              style={{ width: '150px', textAlign: 'center' }}
            />
          </DataTable>
        </div>
      </section>

      {/* NDC Details Modal */}
      <Dialog
        header={`NAD Waiver - ${selectedManufacturer?.manufacturer}`}
        visible={showModal}
        style={{ width: '1000px' }}
        onHide={() => setShowModal(false)}
        modal
        className="nad-waiver-modal"
      >
        {selectedManufacturer && (
          <div className="ndc-modal-content">
            <p className="modal-info">
              Showing {selectedManufacturer.ndc_count} NDC{selectedManufacturer.ndc_count > 1 ? 's' : ''} with active waivers for {selectedManufacturer.manufacturer}
            </p>

            <DataTable
              value={selectedManufacturer.ndcs}
              className="ndc-detail-table"
              stripedRows
              scrollable
              scrollHeight="500px"
            >
              <Column
                field="ndc"
                header="NDC"
                style={{ fontFamily: 'monospace', fontWeight: 600, width: '130px' }}
              />
              <Column
                field="item_description"
                header="Item Description"
                style={{ minWidth: '250px' }}
              />
              <Column
                header="Issue Date"
                body={(rowData) => (
                  <div>{new Date(rowData.issue_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}</div>
                )}
                style={{ width: '120px' }}
              />
              <Column
                header="Expiry Date"
                body={(rowData) => (
                  <div>{new Date(rowData.exp_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}</div>
                )}
                style={{ width: '120px' }}
              />
              <Column
                header="COO Reviewed"
                body={(rowData) => (
                  rowData.date_coo_reviewed ? (
                    <div>{new Date(rowData.date_coo_reviewed).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}</div>
                  ) : '-'
                )}
                style={{ width: '120px' }}
              />
              <Column
                field="applies_to"
                header="Applies To"
                style={{ width: '120px' }}
              />
              <Column
                field="contract_num"
                header="Contract #"
                body={(rowData) => rowData.contract_num || '-'}
                style={{ width: '110px' }}
              />
              <Column
                field="repl_id"
                header="Repl ID"
                style={{ width: '90px' }}
              />
              <Column
                header="Received Date"
                body={(rowData) => (
                  rowData.ods_received_date ? (
                    <div>{new Date(rowData.ods_received_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}</div>
                  ) : '-'
                )}
                style={{ width: '130px' }}
              />
            </DataTable>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default NADWaiverTable;
